import { useState } from "react";
import { Form, Input, Upload, Button, message, Select } from "antd";
import { InboxOutlined } from "@ant-design/icons";
import type { SelectProps } from "antd";
import WizardStepLayout from "./WizardStepLayout";

const { Dragger } = Upload;

interface StepProps {
  loading: boolean;
  gotoStep: Function;
  current: number;
  submit: Function;
}

const tailLayout = {
  labelCol: { span: 8 },
  wrapperCol: { offset: 8, span: 8 },
};

const documentTypes: SelectProps["options"] = [
  {
    name: "ID Document",
    label: "ID Document",
    value: "IdDocument",
  },
  {
    name: "Application Document",
    label: "Application Document",
    value: "ApplicationDocument",
  },
  {
    name: "Policy Document",
    label: "Policy Document",
    value: "PolicyDocument",
  },
  {
    name: "Other",
    label: "Other",
    value: "Other",
  },
];

const Documents: React.FC<StepProps> = ({
  loading,
  gotoStep,
  current,
  submit,
}) => {
  const [base64String, setBase64String] = useState<string>("");
  const [name, setName] = useState<string>("");
  const [type, setType] = useState<any>("");

  const handleFileChange = async (file: any) => {
    try {
      const base64 = await convertFileToBase64(file);
      setBase64String(base64 as string);
      message.success(`${file.name} file is ready for upload.`);
    } catch (error) {
      console.error("Error converting file to base64:", error);
      message.error("Failed to process the file.");
    }
    return false; // Prevents file upload
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
  };

  const handleTypeChange = (value: any) => {
    setType(value);
    if (!name) {
      const label =
        documentTypes.find((opt) => opt?.value === value)?.label ?? String(value);
      setName(String(label));
    }
  };

  const convertFileToBase64 = (file: File) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => resolve(event?.target?.result);
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });
  };

  const content = (
    <WizardStepLayout
      action={
        <Form.Item {...tailLayout} style={{ marginBottom: 0 }}>
          <Button
            loading={loading}
            block
            size="large"
            onClick={() => {
              const resolvedName =
                name ||
                String(
                  documentTypes.find((opt) => opt?.value === type)?.label ?? ""
                );
              if (!base64String || !resolvedName || !type) {
                message.error("Please select a file and enter a name.");
                return;
              }

              submit({
                base64: base64String,
                name: resolvedName,
                documentType: type,
              }).then((result: any) => {
                if (result) {
                  gotoStep(current + 1);
                } else {
                  gotoStep(current + 2);
                }
              });
            }}
          >
            Submit
          </Button>
        </Form.Item>
      }
    >
      <Form.Item name="Document" label="Document">
        <Dragger
          name="file"
          accept=".pdf,.jpg,.png"
          multiple={false}
          beforeUpload={handleFileChange}
          showUploadList={{
            showRemoveIcon: true,
          }}
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">
            Click or drag file to this area to prepare for upload
          </p>
          <p className="ant-upload-hint">
            Supported formats: PDF, JPG, PNG. Only a single file can be
            selected.
          </p>
        </Dragger>
      </Form.Item>

      <Form.Item name="DocumentName" label="Document Name">
        <Input
          size="large"
          id="DocumentName"
          placeholder="Document Name"
          value={name}
          onChange={handleNameChange}
        />
      </Form.Item>

      <Form.Item
        name="DocumentType"
        label="Document Type"
        rules={[
          {
            required: true,
          },
        ]}
      >
        <Select
          style={{ width: "100%" }}
          size="large"
          placeholder="Document Type"
          options={documentTypes}
          onChange={handleTypeChange}
        />
      </Form.Item>
    </WizardStepLayout>
  );

  return content;
};

export default Documents;
